import {
	type GetImpulseFormInput,
	type GetImpulseFormInputSetter,
	ImpulseFormList,
	ImpulseFormShape,
} from "~/tools/impulse-form";
import { setFormElementsInput } from "~/tools/impulse-form/set-form-elements-input";
import { isPresent } from "~/tools/is-present";
import type { Partial } from "~/tools/partial";

import { WidgetConnectNulls } from "./connect-nulls/form-state";
import { WidgetLegend } from "./legend/form-state";
import { WidgetRange } from "./range/form-state";
import { WidgetThresholdsMode, WidgetThresholdsStep } from "./thresholds/form-state";
import { WidgetYAxis } from "./y-axis/form-state";
import type { WidgetFormSync } from "./sync";

type TimeSeriesChartFormState = ImpulseFormShape<{
	connectNulls: WidgetConnectNulls;

	range: ImpulseFormShape<{
		min: WidgetRange;
		max: WidgetRange;
	}>;

	legend: WidgetLegend;

	yAxis: WidgetYAxis;

	thresholds: ImpulseFormShape<{
		mode: WidgetThresholdsMode;
		steps: ImpulseFormList<WidgetThresholdsStep>;
	}>;
}>;

type Input = GetImpulseFormInput<TimeSeriesChartFormState>;

const DEFAULT = {
	connectNulls: WidgetConnectNulls.default,
	range: {
		min: WidgetRange.default,
		max: WidgetRange.default,
	},
	legend: WidgetLegend.default,
	yAxis: WidgetYAxis.default,
	thresholds: {
		mode: WidgetThresholdsMode.default,
		steps: [],
	},
} satisfies Input;

function init(initial?: Partial<Input, "deep">): TimeSeriesChartFormState {
	return ImpulseFormShape({
		connectNulls: WidgetConnectNulls.init(initial?.connectNulls),

		range: ImpulseFormShape({
			min: WidgetRange.init(initial?.range?.min),
			max: WidgetRange.init(initial?.range?.max),
		}),

		legend: WidgetLegend.init(initial?.legend),

		yAxis: WidgetYAxis.init(initial?.yAxis),

		thresholds: ImpulseFormShape({
			mode: WidgetThresholdsMode.init(initial?.thresholds?.mode),
			steps: ImpulseFormList((initial?.thresholds?.steps ?? []).map(WidgetThresholdsStep.init)),
		}),
	});
}

function setInput(
	{ connectNulls, range, legend, yAxis, thresholds }: Partial<Input, "deep">,
	state: TimeSeriesChartFormState,
): void {
	state.setInput({
		connectNulls,
		range,
		legend: {
			enabled: legend?.enabled,
			element: {
				position: legend?.element?.position,
				mode: {
					active: legend?.element?.mode?.active,
					branches: {
						table: legend?.element?.mode?.branches?.table?.filter(isPresent),
					},
				},
			},
		},
		yAxis,

		thresholds: {
			mode: thresholds?.mode,
		},
	});

	if (thresholds?.steps != null) {
		setFormElementsInput(
			(threshold) => WidgetThresholdsStep.init(threshold),
			thresholds.steps.filter(isPresent),
			state.fields.thresholds.fields.steps,
		);
	}
}

function fromSync({ connectNulls, min, max, unit, decimals, legend, yAxis, thresholds }: WidgetFormSync) {
	return {
		connectNulls,

		range: { min, max },

		legend: {
			enabled: legend?.enabled,
			element: {
				position: legend?.position,
				mode: {
					active: legend?.mode,
					branches: {
						table: legend?.table,
					},
				},
			},
		},

		yAxis: {
			enabled: yAxis?.enabled,
			element: {
				decimals,
				unit,
				label: yAxis?.label,
			},
		},

		thresholds: {
			mode: thresholds.mode,
			steps: thresholds.steps,
		},
	} satisfies GetImpulseFormInputSetter<TimeSeriesChartFormState>;
}

function toSync({ connectNulls, range, yAxis, legend, thresholds }: Input) {
	return {
		connectNulls,
		min: range.min,
		max: range.max,
		unit: yAxis.element.unit,
		decimals: yAxis.element.decimals,
		legend: {
			enabled: legend.enabled,
			position: legend.element.position,
			mode: legend.element.mode.active,
			table: legend.element.mode.branches.table,
		},
		yAxis: {
			enabled: yAxis.enabled,
			label: yAxis.element.label,
		},
		thresholds: {
			mode: thresholds.mode,
			steps: thresholds.steps,
		},
	} satisfies WidgetFormSync;
}

const TimeSeriesChartFormState = {
	default: DEFAULT,
	init,
	setInput,
	toSync,
	fromSync,
} as const;

export { TimeSeriesChartFormState };
